import { View, Text, Pressable, Platform } from "react-native";
import { Link } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { GUIDE_PAGES } from "./_components";

/** Card for guide index linking to a feature page */
const GuideIndexCard = ({ href, icon, label, color, description }: {
  href: string;
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  color: string;
  description: string;
}) => (
  <Link href={href as any} asChild>
    <Pressable
      className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md hover:scale-[1.01] transition-all active:scale-[0.99]"
      style={{ cursor: "pointer", minWidth: 280, flex: 1 } as any}
    >
      <View
        className="w-12 h-12 rounded-xl items-center justify-center mb-4"
        style={{ backgroundColor: color + "15" }}
      >
        <Ionicons name={icon} size={24} color={color} />
      </View>
      <Text className="text-lg font-semibold text-gray-900 mb-1">{label}</Text>
      <Text className="text-sm text-gray-500 leading-5">{description}</Text>
      <View className="flex-row items-center mt-3">
        <Text className="text-sm font-medium text-violet-600 mr-1">Lees meer</Text>
        <Ionicons name="arrow-forward" size={14} color="#7C3AED" />
      </View>
    </Pressable>
  </Link>
);

const PAGE_DESCRIPTIONS: Record<string, string> = {
  "/(web)/guide/getting-started": "Download de app, maak een account aan en ontdek het startscherm.",
  "/(web)/guide/weekend-mode": "Hoe het familie weekend werkt, competitie modus en vrij spelen.",
  "/(web)/guide/roles": "De verschillende rollen: familielid, admin en super admin.",
  "/(web)/guide/teams": "Teams, aanvoerders, avatars en samenwerking.",
  "/(web)/guide/agenda": "Evenementen, categorieën, onthullingen en herinneringen.",
  "/(web)/guide/chat": "Berichten, groepschats, polls en media delen.",
  "/(web)/guide/games": "Alle 9 spellen met uitleg, regels en speelmodi.",
  "/(web)/guide/challenges": "Foto-opdrachten, uitdagingen en hoe je punten verdient.",
  "/(web)/guide/points": "Het puntensysteem, teamscores en het leaderboard.",
  "/(web)/guide/speurtocht": "Speurtochten met QR-codes, locaties en puzzels.",
  "/(web)/guide/notifications": "Push-meldingen, in-app notificaties en instellingen.",
  "/(web)/guide/photos": "De familiegalerij, uploaden, likes en reacties.",
};

/**
 * Guide index page with visual cards linking to all feature sections
 */
export default function GuideIndexPage() {
  if (Platform.OS !== "web") return null;

  return (
    <View>
      {/* Hero */}
      <View className="mb-10">
        <View className="flex-row items-center gap-3 mb-4">
          <View className="w-16 h-16 bg-violet-100 rounded-2xl items-center justify-center">
            <Ionicons name="book" size={32} color="#7C3AED" />
          </View>
          <View>
            <Text className="text-3xl font-bold text-gray-900">Handleiding</Text>
            <Text className="text-base text-gray-500 mt-1">Staatsen Familie App</Text>
          </View>
        </View>
        <Text className="text-lg text-gray-600 leading-7">
          Welkom bij de complete handleiding van de Staatsen Familie App! Hier vind je alles wat je 
          moet weten over de app: van het aanmaken van je account tot het spelen van spellen tijdens 
          het familie weekend. Klik op een onderwerp om meer te lezen.
        </Text>
      </View>

      {/* PDF download hint */}
      <View className="bg-violet-50 rounded-xl p-5 border border-violet-200 mb-8">
        <View className="flex-row items-start gap-3">
          <Ionicons name="document-text" size={22} color="#7C3AED" />
          <View className="flex-1">
            <Text className="text-sm font-semibold text-violet-900 mb-1">
              Liever alles in één document?
            </Text>
            <Text className="text-sm text-violet-700 leading-5">
              Ga naar de{" "}
              <Link href="/(web)/guide/full" asChild>
                <Pressable style={{ cursor: "pointer" } as any}>
                  <Text className="text-violet-700 font-semibold underline">
                    volledige handleiding
                  </Text>
                </Pressable>
              </Link>
              {" "}en sla deze op als PDF via je browser.
            </Text>
          </View>
        </View>
      </View>

      {/* Feature grid */}
      <View className="flex-row flex-wrap gap-4">
        {GUIDE_PAGES.map((page) => (
          <GuideIndexCard
            key={page.href}
            {...page}
            description={PAGE_DESCRIPTIONS[page.href] || ""}
          />
        ))}
      </View>
    </View>
  );
}
